import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChartComponent } from "@/types/insights";
import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartCard } from "./InsightsComponentCard";
import {
  buildBarChartData,
  buildChartConfig,
  formatNumber,
  getBarChartSeries,
} from "./insights-component-utils";

export const InsightsBarChartCard = ({ component }: { component: BarChartComponent }) => {
  const series = useMemo(() => getBarChartSeries(component), [component]);
  const data = useMemo(() => buildBarChartData(component, series), [component, series]);
  const config = useMemo(() => buildChartConfig(series), [series]);

  if (data.length === 0 || series.length === 0) return null;

  return (
    <ChartCard title={component.title} description={component.description}>
      <ChartContainer config={config} className="h-80 w-full aspect-auto">
        <BarChart data={data} margin={{ left: 12, right: 12, top: 12 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="category"
            tickLine={false}
            axisLine={false}
            interval={0}
            angle={data.length > 4 ? -20 : 0}
            textAnchor={data.length > 4 ? "end" : "middle"}
            height={64}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: number) =>
              component.yAxis?.unit ? `${formatNumber(value)} ${component.yAxis.unit}` : formatNumber(value)
            }
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => (
                  <span className="font-mono tabular-nums">
                    {typeof value === "number" ? formatNumber(value) : String(value)}
                  </span>
                )}
              />
            }
          />
          {series.length > 1 ? <ChartLegend content={<ChartLegendContent />} /> : null}
          {series.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              fill={`var(--color-${item.key})`}
              radius={4}
              stackId={component.stacked ? "insights-bar-stack" : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
};
